import React, { useState, useEffect, useMemo, useRef } from 'react';
import { printOrShareHtml } from '../lib/print-or-share';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldAlert } from 'lucide-react';
import { format } from 'date-fns';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { doc, setDoc, updateDoc, deleteField, getDocs, collection } from 'firebase/firestore';
import { deepClean } from '../lib/utils';
import { saveLocalFirst } from '../lib/save-local-first';
import { loadPdfPages } from '../lib/pdf/pdfText';
import { parseLineupCard } from '../lib/pdf/lineupCardParser';
import { generateOptimalLineup, sortBattingOrder } from '../lib/lineup-engine';
import { aggregateBatterSignals, suggestBattingOrder, BatterSeasonSignal } from '../lib/insights/season-signals';
import { defenseSettingsFor } from '../lib/batting-order';
import { buildSeasonStats } from '../lib/season-stats';
import { buildOpponentScout, OpponentScoutReport } from '../lib/insights/opponent-signals';
import { PITCH_COUNT_RULESETS, getPitcherEligibility } from '../lib/pitch-count-rules';
import { tapLight, tapMedium } from '../lib/haptics';
import { buildPitcherReadiness } from '../lib/pitcher-readiness';
import { PlanEntry } from './plan/PlanEntry';
import { AvailabilityScreen } from './plan/AvailabilityScreen';
import { StepPitcher } from './plan/StepPitcher';
import { StepDefense } from './plan/StepDefense';
import { StepOrder } from './plan/StepOrder';
import { StepBullpen, plannedPitcherByInning } from './plan/StepBullpen';
import { StepReview } from './plan/StepReview';
import { ManualPlan } from './plan/ManualPlan';
import { SeasonFieldTime, buildFieldTimeRows } from './plan/SeasonFieldTime';
import { WizardBar } from './plan/WizardChrome';
import { swapIntoPosition } from '../lib/game-positions';
import { fieldTimeSourceOf } from '../lib/gc-innings';
import { PlanConsequences } from './plan/PlanConsequences';
import { useIsWide } from '../hooks/useIsWide';
import { stepSequenceFor, stepLabelFor, nextScreen, prevScreen } from './plan/steps';

/**
 * Screens in the Game Planner. The five numbered steps are the coach's own
 * decision order; `entry`, `availability`, `fieldtime` and `scouting` sit off to
 * the side and are reached from within a step rather than sequenced.
 */
export type PlanScreen =
  | 'entry' | 'availability'
  | 'pitcher' | 'defense' | 'order' | 'bullpen' | 'review'
  | 'fieldtime' | 'scouting' | 'manual';

/** Opponent-key normalization — MUST match reconcileGameImport's opponentKeyOf. */
const normKey = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Bounds for the planner's "+ Inning" / "- Inning" controls. */
const MIN_PLAN_INNINGS = 1;
const MAX_PLAN_INNINGS = 12;

// Order and defense mode lists now live in src/lib/batting-order.ts, and the
// wizard steps import them directly, so this screen no longer keeps its own.

// ── Scouting Tab (opponent intel only) ────────────────────────────────────────

const ScoutingTab = ({ opponentScout, game }: {
  opponentScout: OpponentScoutReport | null;
  game: any;
}) => {
  const eyebrow: React.CSSProperties = {
    fontSize: '11px', fontWeight: 600, color: '#888888',
    textTransform: 'uppercase', letterSpacing: '0.07em',
  };
  const rowBase: React.CSSProperties = {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    padding: '13px 16px', borderBottom: '0.5px solid #E0DED9',
    backgroundColor: '#FFFFFF',
  };

  const scoutPitchers = (opponentScout?.pitchers || []).filter(p => p.battersFaced >= 3).slice(0, 3);
  const dangerBats = (opponentScout?.batters || []).filter(b => b.danger).slice(0, 4);
  const softBats = (opponentScout?.batters || []).filter(b => b.softSpot).slice(0, 3);
  const runners = (opponentScout?.batters || []).filter(b => b.sb >= 1).sort((a, b) => b.sb - a.sb).slice(0, 4);
  const hasScout = !!opponentScout && (scoutPitchers.length > 0 || dangerBats.length > 0 || softBats.length > 0 || runners.length > 0);
  const slotStr = (b: { topSlot: number | null }) => b.topSlot ? `#${b.topSlot} hitter` : 'their order';

  if (!hasScout) {
    return (
      <div style={{ backgroundColor: '#FFFFFF', padding: '48px 24px', textAlign: 'center' }}>
        <ShieldAlert style={{ width: '32px', height: '32px', color: '#DDDDDD', margin: '0 auto 12px' }} />
        <p style={{ fontSize: '14px', fontWeight: 600, color: '#888888' }}>No intel on {game.opponent} yet.</p>
        <p style={{ fontSize: '12px', fontWeight: 400, color: '#AAAAAA', marginTop: '6px', lineHeight: 1.6 }}>
          Import a GameChanger scorebook from a prior meeting{'\n'}to unlock scouting data.
        </p>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#FFFFFF' }}>
      <div style={{ padding: '14px 16px 6px', borderBottom: '0.5px solid #E0DED9', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span style={eyebrow}>vs {game.opponent}</span>
        <span style={{ fontSize: '11px', fontWeight: 400, color: '#AAAAAA' }}>
          {opponentScout!.games} game{opponentScout!.games === 1 ? '' : 's'}{opponentScout!.thinSample ? ' · thin sample' : ''}
        </span>
      </div>

      {scoutPitchers.length > 0 && (
        <>
          <div style={{ padding: '12px 16px 4px' }}>
            <span style={{ ...eyebrow, fontSize: '10px', color: '#AAAAAA' }}>Their Arms</span>
          </div>
          {scoutPitchers.map((p, i) => {
            let read = '';
            if (p.firstPitchStrikePct >= 0.65 && p.walkRate < 0.12) read = `Pounds the zone (${Math.round(p.firstPitchStrikePct * 100)}% first-pitch strikes). Be ready to swing early.`;
            else if (p.commandCollapse) read = `Gets ahead but loses it: ${Math.round(p.firstPitchStrikePct * 100)}% first-pitch strikes, then ${p.walks} walks. Patience may pay.`;
            else if (p.fatigue.detected) read = `Faded last time. Strikes dropped ${Math.round(p.fatigue.earlyStrikePct * 100)}% to ${Math.round(p.fatigue.lateStrikePct * 100)}%. Work the count.`;
            else if (p.walkRate >= 0.18) read = `Has trouble with the zone (${p.walks} walks). Make him throw strikes.`;
            else read = `${Math.round(p.firstPitchStrikePct * 100)}% first-pitch strikes, ${Math.round(p.strikePct * 100)}% overall.`;
            return (
              <div key={i} style={rowBase}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#0D0D0D' }}>#{p.number} {p.name}</span>
                    <span style={{ fontSize: '12px', fontWeight: 400, color: '#AAAAAA' }}>{p.battersFaced} batters faced</span>
                  </div>
                  <p style={{ fontSize: '13px', fontWeight: 400, color: '#444444', lineHeight: 1.55, marginTop: '3px' }}>{read}</p>
                </div>
              </div>
            );
          })}
        </>
      )}

      {dangerBats.length > 0 && (
        <>
          <div style={{ padding: '12px 16px 4px' }}>
            <span style={{ ...eyebrow, fontSize: '10px', color: '#AAAAAA' }}>Watch These Bats</span>
          </div>
          {dangerBats.map((b, i) => (
            <div key={i} style={rowBase}>
              <p style={{ fontSize: '13px', fontWeight: 400, color: '#444444', lineHeight: 1.55 }}>
                <span style={{ fontWeight: 600, color: '#0D0D0D' }}>#{b.number} {b.name}</span>
                {' '}({slotStr(b)}), {b.xbh >= 2 ? `${b.xbh} extra-base hits against us` : `on base ${Math.round(b.onBasePct * 100)}% of the time`}{b.sb >= 1 ? `, ${b.sb} steal${b.sb === 1 ? '' : 's'}` : ''}.
              </p>
            </div>
          ))}
        </>
      )}

      {softBats.length > 0 && (
        <>
          <div style={{ padding: '12px 16px 4px' }}>
            <span style={{ ...eyebrow, fontSize: '10px', color: '#AAAAAA' }}>We've Handled</span>
          </div>
          {softBats.map((b, i) => (
            <div key={i} style={rowBase}>
              <p style={{ fontSize: '13px', fontWeight: 400, color: '#444444', lineHeight: 1.55 }}>
                <span style={{ fontWeight: 600, color: '#0D0D0D' }}>#{b.number} {b.name}</span>
                {' '}({slotStr(b)}), struck out {b.strikeouts} of {b.pa} plate appearances.
              </p>
            </div>
          ))}
        </>
      )}

      {runners.length > 0 && (
        <>
          <div style={{ padding: '12px 16px 4px' }}>
            <span style={{ ...eyebrow, fontSize: '10px', color: '#AAAAAA' }}>Running Game</span>
          </div>
          <div style={rowBase}>
            <p style={{ fontSize: '13px', fontWeight: 400, color: '#444444', lineHeight: 1.55 }}>
              Watch for the steal from{' '}
              {runners.map((b, i) => (
                <span key={i}>
                  <span style={{ fontWeight: 600, color: '#0D0D0D' }}>#{b.number} {b.name}</span>{' '}({b.sb} SB){i < runners.length - 1 ? ', ' : ''}
                </span>
              ))}. Keep your catcher ready.
            </p>
          </div>
        </>
      )}
    </div>
  );
};


// ── Main Component ─────────────────────────────────────────────────────────────

// `onBeginGame` is optional on purpose: when the planner is opened from a screen
// that can start a game, the review card offers "Begin Game"; when it is opened
// to plan ahead, that button reads "Mark Ready" instead. Both save either way.
export const PlanGameView = ({ game, team, players, playerSeasonStats, playerFieldTimeStats, onClose, isElite = false, onUpgrade, initialTab, onPlayerTap, onBeginGame }: any) => {
  const [innings, setInnings] = useState<any[]>([]);
  // Surfaced when print/share fails. Previously a failed export was a silent
  // no-op, which on native is indistinguishable from a dead button.
  const [shareError, setShareError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [numInnings, setNumInnings] = useState(team.defaultInnings || 6);
  const [planPlayers, setPlanPlayers] = useState<any[]>([]);
  const [absentPlayerIds, setAbsentPlayerIds] = useState<string[]>(game.plan?.absentPlayerIds || []);
  // ── Wizard navigation ─────────────────────────────────────────────────────
  // Replaces the six peer tabs. `entry` is the front door; a game that already
  // has a saved plan opens on `review` instead, so a returning coach sees his
  // plan rather than being walked through the wizard again.
  // A plan saved from the manual planner reopens there: a coach who skipped
  // the wizard once should not be walked back through it.
  const [plannerMode, setPlannerMode] = useState<'wizard' | 'manual'>(
    game.plan?.mode === 'manual' ? 'manual' : 'wizard',
  );
  const [screen, setScreen] = useState<PlanScreen>(
    initialTab === 'scouting'
      ? 'scouting'
      : game.plan?.savedAt ? (game.plan?.mode === 'manual' ? 'manual' : 'review') : 'entry',
  );
  // Where a side screen (availability, bullpen, scouting) returns to when it
  // was opened from the manual planner rather than from its usual place.
  const [backTo, setBackTo] = useState<PlanScreen | null>(null);
  const [allGames, setAllGames] = useState<any[]>([]);
  // Relief slots 2 and 3. Slot 1 is always `selectedPitcherId`, so the starter
  // cannot drift out of sync with the bullpen plan.
  const [relievers, setRelievers] = useState<(string | null)[]>(() => {
    const saved: string[] = game.plan?.pitcherPlan || [];
    return [saved[1] ?? null, saved[2] ?? null];
  });

  const [selectedPitcherId, setSelectedPitcherId] = useState<string | null>(
    game.plan?.startingPitcherId || null
  );
  const [alertModal, setAlertModal] = useState<{ title: string; message: string } | null>(null);
  const [isPitcherDropdownOpen, setIsPitcherDropdownOpen] = useState(false);
  const [showMorePlanPitchers, setShowMorePlanPitchers] = useState(false);
  const [orderMode, setOrderMode] = useState<string>('None');
  const [isOrderDropdownOpen, setIsOrderDropdownOpen] = useState(false);
  const [batterSignals, setBatterSignals] = useState<Map<string, BatterSeasonSignal>>(new Map());
  const [opponentScout, setOpponentScout] = useState<OpponentScoutReport | null>(null);
  const [orderNote, setOrderNote] = useState<string>('');
  const [defenseMode, setDefenseMode] = useState<string>('smart');
  // Which inning the defense grid brings into view on arrival (0-based): the
  // one the coach tapped on the review card, or inning 1.
  const [defenseInning, setDefenseInning] = useState(0);
  // Set once the coach hand-edits any inning, so changing the fill mode asks
  // before rebuilding over his changes. Cleared by every fill.
  const [defenseEdited, setDefenseEdited] = useState(false);
  // Set when the coach removes an inning, so the next save also deletes it
  // from Firestore (see clearRemovedInnings).
  const inningsTrimmed = useRef(false);
  const [isDefenseDropdownOpen, setIsDefenseDropdownOpen] = useState(false);

  useEffect(() => {
    if (game.status === 'completed' && game.inningLogs && Object.keys(game.inningLogs).length > 0) {
      const loadedInnings: any[] = [];
      Object.entries(game.inningLogs).forEach(([key, value]) => {
        const idx = parseInt(key) - 1;
        if (idx >= 0) {
          loadedInnings[idx] = value;
        }
      });
      setInnings(loadedInnings);
      setNumInnings(Math.max(game.plan?.numInnings || 6, loadedInnings.length));

      if (game.plan?.playerOrder) {
        const ordered = game.plan.playerOrder.map((id: string) => players.find((p: any) => p.id === id)).filter(Boolean);
        const remaining = players.filter((p: any) => !game.plan.playerOrder.includes(p.id));
        setPlanPlayers([...ordered, ...remaining]);
      } else {
        setPlanPlayers([...players].sort((a, b) => (b.ovr || 0) - (a.ovr || 0)));
      }
    } else if (game.plan) {
      let loadedInnings = game.plan.innings || [];
      if (loadedInnings && !Array.isArray(loadedInnings)) {
        const inningsArray: any[] = [];
        Object.entries(loadedInnings).forEach(([key, value]) => {
          inningsArray[parseInt(key)] = value;
        });
        loadedInnings = inningsArray;
      }
      setInnings(loadedInnings);
      setNumInnings(game.plan.numInnings || 6);
      if (game.plan.playerOrder) {
        const ordered = game.plan.playerOrder.map((id: string) => players.find((p: any) => p.id === id)).filter(Boolean);
        const remaining = players.filter((p: any) => !game.plan.playerOrder.includes(p.id));
        setPlanPlayers([...ordered, ...remaining]);
      } else {
        setPlanPlayers([...players].sort((a, b) => (b.ovr || 0) - (a.ovr || 0)));
      }
    } else {
      setPlanPlayers([...players].sort((a, b) => (b.ovr || 0) - (a.ovr || 0)));
    }
  }, [players, game]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const gamesSnap = await getDocs(collection(db, `teams/${team.id}/games`));
        // Keep the game docs too: the wizard needs them for "Use last game" and
        // for each pitcher's last-outing line. Same read, no extra round trip.
        // Exclude the onboarding CSV's synthesized stats-container doc — it's
        // not a game the coach played and must never be offered as "last game".
        const gameDocs = gamesSnap.docs
          .map(g => ({ id: g.id, ...(g.data() as any) }))
          .filter((g: any) => g.source !== 'onboardingImport');
        // Publish the games BEFORE the at-bat fan-out. Steps 1 and 4 need only
        // these to show rest, season pitching and the innings forecast, and the
        // per-game atBats reads take seconds across a full season, which would
        // otherwise leave the pitcher step looking like it has no data at all.
        if (cancelled) return;
        setAllGames(gameDocs);

        const all: any[] = [];
        await Promise.all(gamesSnap.docs.map(async (g) => {
          const abSnap = await getDocs(collection(db, `teams/${team.id}/games/${g.id}/atBats`));
          abSnap.forEach((d) => all.push({ ...(d.data() as any), gameId: g.id }));
        }));
        if (cancelled) return;
        setBatterSignals(aggregateBatterSignals(all as any));

        const target = normKey(game.opponent || '');
        const keys = [...new Set(all.map(a => a.opponentKey).filter(Boolean))] as string[];
        const matchKey = target
          ? keys.find(k => k === target || k.includes(target) || target.includes(k))
          : undefined;
        setOpponentScout(matchKey ? buildOpponentScout(all as any, matchKey) : null);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [team.id, game.opponent]);

  const handleApplyOrder = (mode: string) => {
    const active = planPlayers
      .filter((p: any) => !absentPlayerIds.includes(p.id))
      .map((p: any) => ({ ...p, stats: playerSeasonStats?.get(p.id) ?? p.stats }));
    const absent = planPlayers.filter((p: any) => absentPlayerIds.includes(p.id));

    if (mode === 'Get Baserunners' || mode === 'Wear Down Pitcher') {
      const sugg = suggestBattingOrder(
        batterSignals,
        active.map((p: any) => p.id),
        (id: string) => active.find((p: any) => p.id === id)?.name || '',
        mode === 'Wear Down Pitcher' ? 'weardown' : 'onbase',
      );
      setOrderNote(sugg.note);
      const rank = new Map(sugg.slots.map((s, i) => [s.playerId, i]));
      const ordered = [...active].sort((a: any, b: any) => (rank.get(a.id) ?? 999) - (rank.get(b.id) ?? 999));
      setPlanPlayers([...ordered, ...absent]);
      return;
    }

    setOrderNote('');
    const ordered = mode === 'None'
      ? [...active].sort((a: any, b: any) => (b.ovr || 0) - (a.ovr || 0))
      : sortBattingOrder([...active], mode);
    setPlanPlayers([...ordered, ...absent]);
  };

  const handleReorderActive = (newActiveOrder: any[]) => {
    tapLight(); // a soft tick as a player shifts in the batting order
    const absent = planPlayers.filter((p: any) => absentPlayerIds.includes(p.id));
    setPlanPlayers([...newActiveOrder, ...absent]);
  };

  const handleToggleAbsent = (playerId: string) => {
    setAbsentPlayerIds(prev =>
      prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
    );
  };

  /**
   * Build one inning with the engine, given the innings before it (for
   * fairness and pitcher re-entry) and who is pitching it. Shared by the full
   * fill and by adding a single inning to the end of the plan.
   */
  const buildInning = (
    inning: number, previous: any[][], pitcherId: string | null, effectiveSettings: any,
  ): any[] => {
    const settings = team.settings || {};
    const previousInningsLogs: Record<number, any[]> = {};
    previous.forEach((pInning, idx) => { previousInningsLogs[idx + 1] = pInning; });

    // Honor the coach's Tournament Bench Plan picks only in inning 1
    // (who STARTS on the bench); later innings rotate by fairness.
    const startingBenchIds: string[] = inning === 0 ? (game.plan?.startingBenchIds || []) : [];

    const activePlayers = planPlayers
      .filter((p: any) => !absentPlayerIds.includes(p.id))
      .map((p: any) => ({ ...p, isCurrentPitcher: p.id === pitcherId }));

    const { lineup: inningLineup } = generateOptimalLineup(
      activePlayers,
      effectiveSettings,
      previousInningsLogs,
      game.date,
      game.tournamentId,
      team.styleOfPlay,
      startingBenchIds
    );

    let simplifiedInning = inningLineup.map((p: any) => ({ id: p.id, position: p.position }));

    if (pitcherId && !settings.noPitcher && (inning === 0 || planHasPitchCounts)) {
      // A swap, so whoever the engine had on the mound takes the planned
      // pitcher's spot instead of leaving it empty.
      if (simplifiedInning.some((p: any) => p.id === pitcherId)) {
        simplifiedInning = swapIntoPosition(simplifiedInning, 'P', pitcherId);
      }
    }
    return simplifiedInning;
  };

  /**
   * `starterId` exists for the same reason handleSave's overrides do: Auto
   * Generate picks a starter and fills in the same tick, before
   * setSelectedPitcherId has landed, so the fill must be told who starts.
   */
  const handleFillDefense = (mode: string, starterId: string | null = selectedPitcherId) => {
    setIsGenerating(true);
    setTimeout(() => {
      try {
        const settings = team.settings || {};
        const effectiveSettings = defenseSettingsFor(mode, settings);

        // Who pitches each inning. On players-pitch teams this follows the
        // bullpen plan, so a reliever planned for inning 4 is built into the
        // defense there and the starter moves to the field. Previously the
        // starter was locked at P for the whole game whatever the plan said.
        const armsByInning = planHasPitchCounts
          ? plannedArmsByInning(starterId, relievers)
          : Array.from({ length: numInnings }, () => starterId);

        // Every remaining defense mode regenerates the whole plan from inning 1
        // ('fill_rest' — the only mode that locked inning 1 and filled the rest
        // around it — was removed 2026-09-10).
        const plan: any[] = [];
        for (let inning = 0; inning < numInnings; inning++) {
          plan.push(buildInning(inning, plan, armsByInning[inning] ?? null, effectiveSettings));
        }

        setInnings(plan);
        setDefenseEdited(false);
      } catch (err) {
        console.error('Failed to fill defense', err);
      } finally {
        setIsGenerating(false);
      }
    }, 100);
  };

  const handlePrint = async () => {
    const sp = selectedPitcher;
    const dateStr = format(new Date(game.date), 'EEEE, MMMM d, yyyy');
    const rows = activePlayers.map((player: any) => {
      const isSP = player.id === selectedPitcherId;
      const positions = Array.from({ length: numInnings }).map((_, i) => {
        const pos = innings[i]?.find((p: any) => p.id === player.id)?.position || 'BENCH';
        return pos === 'BENCH' ? '—' : pos;
      });
      const stats = playerSeasonStats?.get(player.id);
      return { player, isSP, positions, stats };
    });

    const colWidth = Math.min(52, Math.floor(480 / numInnings));

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Game Plan, ${game.isHome ? 'vs' : '@'} ${game.opponent}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif;
      background: #fff;
      color: #0D0D0D;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    @page {
      size: letter landscape;
      margin: 0.5in 0.45in;
    }
    .page { width: 100%; }

    /* ── Header ── */
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 10px;
      border-bottom: 1.5px solid #0D0D0D;
      margin-bottom: 14px;
    }
    .header-left { display: flex; align-items: center; gap: 12px; }
    .team-logo { width: 44px; height: 44px; object-fit: contain; }
    .team-name {
      font-size: 18pt; font-weight: 700;
      text-transform: uppercase; letter-spacing: -0.3px;
      line-height: 1.1; color: #0D0D0D;
    }
    .game-meta { font-size: 8pt; font-weight: 400; color: #888; margin-top: 2px; }
    .header-right { text-align: right; }
    .header-date { font-size: 9pt; font-weight: 600; color: #0D0D0D; }
    .header-sub  { font-size: 8pt; font-weight: 400; color: #888; margin-top: 2px; }

    /* ── SP strip ── */
    .sp-strip {
      display: flex; align-items: center; gap: 10px;
      padding: 8px 12px;
      border: 0.5px solid #E0DED9;
      border-radius: 8px;
      margin-bottom: 12px;
      background: #FAFAFA;
    }
    .sp-avatar {
      width: 30px; height: 30px; border-radius: 50%;
      background: #0D0D0D; color: #fff;
      display: flex; align-items: center; justify-content: center;
      font-size: 9pt; font-weight: 600; flex-shrink: 0;
    }
    .sp-label { font-size: 7pt; font-weight: 600; color: #888; text-transform: uppercase; letter-spacing: 0.07em; }
    .sp-name  { font-size: 11pt; font-weight: 600; color: #0D0D0D; }

    /* ── Lineup table ── */
    table { width: 100%; border-collapse: collapse; }
    thead tr { border-bottom: 1px solid #0D0D0D; }
    th {
      font-size: 7pt; font-weight: 600; color: #888;
      text-transform: uppercase; letter-spacing: 0.07em;
      padding: 4px 6px; text-align: center;
    }
    th.player-col { text-align: left; padding-left: 8px; }
    th.stat-col { width: 38pt; }
    th.inn-col  { width: ${colWidth}pt; border-left: 0.5px solid #E0DED9; }

    tbody tr { border-bottom: 0.5px solid #E0DED9; }
    tbody tr.sp-row { border-left: 2.5pt solid #C9A84C; }
    tbody tr:last-child { border-bottom: none; }

    td { padding: 7px 6px; text-align: center; vertical-align: middle; }
    td.num    { font-size: 8pt; font-weight: 400; color: #888; width: 28pt; }
    td.name   { text-align: left; padding-left: 8px; }
    td.pos    { font-size: 9pt; font-weight: 600; color: #0D0D0D; border-left: 0.5px solid #E0DED9; }
    td.bench  { font-size: 9pt; font-weight: 400; color: #C8C5BF; border-left: 0.5px solid #E0DED9; background: #FAFAFA; }

    .player-name { font-size: 10pt; font-weight: 600; color: #0D0D0D; }
    .player-pos  { font-size: 7pt; font-weight: 400; color: #888; margin-top: 1px; }
    .sp-tag {
      display: inline-block; margin-left: 6px;
      font-size: 6.5pt; font-weight: 600; color: #C9A84C;
      background: #F5EDD6; padding: 1px 4px; border-radius: 3px;
      vertical-align: middle;
    }

    .stat-val   { font-size: 9pt; font-weight: 600; color: #0D0D0D; }
    .stat-dim   { font-size: 9pt; font-weight: 400; color: #C8C5BF; }
    .stat-label { font-size: 6pt; font-weight: 400; color: #AAAAAA; text-transform: uppercase; letter-spacing: 0.04em; margin-top: 1px; }

    /* ── Footer ── */
    .footer {
      margin-top: 14px; padding-top: 8px;
      border-top: 0.5px solid #E0DED9;
      display: flex; justify-content: space-between; align-items: center;
    }
    .footer-brand { font-size: 7pt; font-weight: 600; color: #AAAAAA; text-transform: uppercase; letter-spacing: 0.1em; }
    .footer-status { font-size: 7pt; font-weight: 600; color: #888; text-transform: uppercase; letter-spacing: 0.07em; }
    .absent-note { font-size: 7pt; font-weight: 400; color: #AAAAAA; }
  </style>
</head>
<body>
<div class="page">

  <!-- Header -->
  <div class="header">
    <div class="header-left">
      ${team.logoURL ? `<img class="team-logo" src="${team.logoURL}" />` : ''}
      <div>
        <div class="team-name">${team.name}</div>
        <div class="game-meta">${game.isHome ? 'vs' : '@'} ${game.opponent}</div>
      </div>
    </div>
    <div class="header-right">
      <div class="header-date">${dateStr}</div>
      ${game.time ? `<div class="header-sub">${game.time}</div>` : ''}
    </div>
  </div>

  <!-- SP strip -->
  ${sp ? `
  <div class="sp-strip">
    <div class="sp-avatar">#${sp.number}</div>
    <div>
      <div class="sp-label">Starting Pitcher</div>
      <div class="sp-name">${sp.name}</div>
    </div>
  </div>` : ''}

  <!-- Lineup table -->
  <table>
    <thead>
      <tr>
        <th style="width:22pt">#</th>
        <th class="player-col">Player</th>
        ${Array.from({ length: numInnings }).map((_, i) => `<th class="inn-col">Inn ${i + 1}</th>`).join('')}
      </tr>
    </thead>
    <tbody>
      ${rows.map(({ player, isSP, positions }) => {
        const posStr = [player.primaryPosition, player.secondaryPosition].filter(Boolean).join(' · ');
        return `<tr class="${isSP ? 'sp-row' : ''}">
          <td class="num">${player.number}</td>
          <td class="name">
            <div class="player-name">${player.name}${isSP ? '<span class="sp-tag">SP</span>' : ''}</div>
            ${posStr ? `<div class="player-pos">${posStr}</div>` : ''}
          </td>
          ${positions.map(pos => `<td class="${pos === '—' ? 'bench' : 'pos'}">${pos}</td>`).join('')}
        </tr>`;
      }).join('')}
    </tbody>
  </table>

  <!-- Absent players -->
  ${absentPlayers.length > 0 ? `
  <div style="margin-top: 10px;">
    <span class="absent-note">Absent: ${absentPlayers.map((p: any) => `#${p.number} ${p.name}`).join(' · ')}</span>
  </div>` : ''}

  <!-- Footer -->
  <div class="footer">
    <span class="footer-brand">dugoutbaseball.app</span>
    <span class="footer-status">${game.plan?.isReady ? 'Ready' : 'Draft'} · Printed ${format(new Date(), 'MMM d, yyyy')}</span>
  </div>

</div>
</body>
</html>`;

    // One HTML source, two handoffs: print on web, share a PDF on iOS. Native
    // used to have neither, because window.print() is inert in a WKWebView.
    try {
      setShareError(null);
      await printOrShareHtml(html, {
        fileName: `game-plan-${game.isHome ? 'vs' : 'at'}-${game.opponent || 'opponent'}`,
        title: `Game Plan ${game.isHome ? 'vs' : '@'} ${game.opponent || ''}`.trim(),
        orientation: 'landscape',
        webWindow: { width: 900, height: 700 },
      });
    } catch (e: any) {
      setShareError(e?.message || 'Could not prepare the game plan.');
    }
  };

  const handleCloseWithAutosave = async () => {
    try { await handleSave(false); } catch {}
    onClose();
  };

  /**
   * `overrides` exists because React state is not readable in the same tick it
   * is set. When a coach picks a starter from the detail sheet we both select
   * him AND advance, and without an override this save would persist the
   * PREVIOUS pitcher from a stale closure.
   */
  /**
   * Saves merge into the game doc, and a merge never removes a map key, so an
   * inning the coach removed would survive in plan.innings (and inningLogs) and
   * the live game, which counts those keys to know the game's length, would
   * still expect it. Delete every key past the current count explicitly.
   */
  const clearRemovedInnings = async () => {
    const savedKeys = Object.keys(game.plan?.innings || {}).map(Number);
    if (!inningsTrimmed.current && !savedKeys.some(k => k >= numInnings)) return;
    const deletes: Record<string, any> = {};
    for (let i = numInnings; i < Math.max(MAX_PLAN_INNINGS, ...savedKeys.map(k => k + 1)); i++) {
      deletes[`plan.innings.${i}`] = deleteField();
      if (game.status === 'completed') deletes[`inningLogs.${i + 1}`] = deleteField();
    }
    await saveLocalFirst(updateDoc(doc(db, `teams/${team.id}/games`, game.id), deletes), 'trim innings');
    inningsTrimmed.current = false;
  };

  const handleSave = async (isReady = false, overrides?: { startingPitcherId?: string | null }) => {
    // Locking the lineup in ("Ready") is the deliberate confirm — a firm thump.
    // Saving a draft (isReady false) stays silent so it doesn't feel like every
    // auto-save buzzes.
    if (isReady) tapMedium();
    try {
      const inningsObject: Record<string, any> = {};
      const inningLogsObject: Record<string, any> = {};

      innings.forEach((inning, idx) => {
        if (inning && idx < numInnings) {
          inningsObject[idx.toString()] = inning;
          inningLogsObject[(idx + 1).toString()] = inning;
        }
      });

      const updateData: any = {
        plan: {
          innings: inningsObject,
          numInnings,
          playerOrder: planPlayers.map(p => p.id),
          startingPitcherId: overrides?.startingPitcherId !== undefined
            ? overrides.startingPitcherId
            : selectedPitcherId,
          // Ordered 1st, 2nd, 3rd. Additive: games saved before the bullpen step
          // existed simply read back an empty array. Nulls are stripped because
          // Firestore rejects undefined and a hole carries no meaning here.
          pitcherPlan: [
            overrides?.startingPitcherId !== undefined ? overrides.startingPitcherId : selectedPitcherId,
            ...relievers,
          ].filter(Boolean),
          absentPlayerIds,
          isReady,
          // Which planner the coach used, so the plan reopens the same way.
          mode: plannerMode,
          savedAt: new Date().toISOString()
        }
      };

      if (game.status === 'completed') {
        updateData.inningLogs = inningLogsObject;
      }

      await setDoc(doc(db, `teams/${team.id}/games`, game.id), updateData, { merge: true });
      await clearRemovedInnings();
      if (isReady) onClose();
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `teams/${team.id}/games/${game.id}`);
    }
  };

  /**
   * Hand the plan to the live engine.
   *
   * The engine seeds its lineup from `game.lineup` (see LiveGameEngine's
   * useState), which the wizard never wrote: it only ever saved `game.plan`.
   * So a game planned here used to open on an empty field with an empty bench.
   * Build the engine's shape explicitly instead:
   *
   *   - batting order comes from planPlayers (the order step's own order),
   *     with absent players dropped
   *   - each player's inning-1 defensive position comes from innings[0],
   *     defaulting to BENCH so nobody is ever left without a position
   *   - status flips to in_progress, matching what the Lineup Builder's
   *     "Play Ball" writes, so the game reads as live everywhere else
   *
   * The saved object is passed forward, NOT the stale `game` prop, because the
   * prop predates this write and would hand the engine an empty lineup again.
   */
  const handleBeginGame = async () => {
    let inningOne: any[] = innings[0] || [];

    // A plan can reach the review card with no defense filled in (the defense
    // step is skippable, and a plan saved before any Fill still has an empty
    // inning 1). Starting from that would drop all nine fielders onto the
    // bench and open the engine on an empty diamond, so solve one here first.
    const hasFielders = inningOne.some(
      (e: any) => e.position && e.position !== 'BENCH' && e.position !== 'EH',
    );
    if (!hasFielders) {
      try {
        const active = planPlayers
          .filter((p: any) => !absentPlayerIds.includes(p.id))
          .map((p: any) => ({ ...p, isCurrentPitcher: p.id === selectedPitcherId }));
        const { lineup: solved } = generateOptimalLineup(
          active,
          defenseSettingsFor(defenseMode, team.settings || {}),
          {},
          game.date,
          game.tournamentId,
          team.styleOfPlay,
          game.plan?.startingBenchIds || [],
        );
        inningOne = solved.map((p: any) => ({ id: p.id, position: p.position }));
        // Keep the chosen starter on the mound; the solver does not know him.
        if (selectedPitcherId && !team.settings?.noPitcher) {
          const atP = inningOne.find((e: any) => e.position === 'P');
          const starter = inningOne.find((e: any) => e.id === selectedPitcherId);
          if (starter && atP && atP.id !== selectedPitcherId) {
            const was = starter.position;
            starter.position = 'P';
            atP.position = was;
          }
        }
      } catch (err) {
        console.error('Failed to solve a defense for Begin Game', err);
      }
    }

    const positionOf = (id: string) =>
      inningOne.find((e: any) => e.id === id)?.position || 'BENCH';

    const lineup = planPlayers
      .filter((p: any) => !absentPlayerIds.includes(p.id))
      .map((p: any) => ({ ...p, position: positionOf(p.id) }));

    const inningsObject: Record<string, any> = {};
    innings.forEach((inning, idx) => { if (inning && idx < numInnings) inningsObject[idx.toString()] = inning; });
    inningsObject['0'] = inningOne;

    const startedGame = {
      ...game,
      lineup,
      absentPlayerIds,
      status: 'in_progress',
      plan: {
        innings: inningsObject,
        numInnings,
        playerOrder: planPlayers.map((p: any) => p.id),
        startingPitcherId: selectedPitcherId,
        pitcherPlan: [selectedPitcherId, ...relievers].filter(Boolean),
        absentPlayerIds,
        isReady: true,
        mode: plannerMode,
        savedAt: new Date().toISOString(),
      },
    };

    try {
      // `lineup` spreads full player objects (`...p`). Firestore's Web SDK
      // rejects any undefined value outright, so clean the payload here as a
      // defensive backstop in case a player object ever carries one.
      // saveLocalFirst so Begin Game works at a field with no signal.
      await saveLocalFirst(setDoc(
        doc(db, `teams/${team.id}/games`, game.id),
        deepClean({ lineup, absentPlayerIds, status: 'in_progress', plan: startedGame.plan }),
        { merge: true },
      ), 'Begin Game');
      await clearRemovedInnings();
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `teams/${team.id}/games/${game.id}`);
      return;
    }

    if (onBeginGame && game.status !== 'completed') onBeginGame(startedGame);
    else onClose();
  };

  // ── Lineup card export (GC-style portrait) ────────────────────────────────
  const handleLineupCardExport = async () => {
    const activePlayers = planPlayers.filter((p: any) => !absentPlayerIds.includes(p.id));
    const dateStr = format(new Date(game.date), 'EEEE, MMMM d, yyyy');
    // Read each player's actual first-inning defensive assignment from the
    // generated plan so the card is a VALID fielding lineup (one player per
    // position, extras on the bench). Falls back to primary position only when no
    // defense has been generated yet, so the card still prints before "Fill".
    const inning1: any[] | null = innings[0] && innings[0].length > 0 ? innings[0] : null;
    const rows = activePlayers.map((player: any, idx: number) => {
      const isSP = player.id === selectedPitcherId;
      let pos: string;
      if (isSP) {
        pos = 'P';
      } else if (inning1) {
        const assigned = inning1.find((p: any) => p.id === player.id)?.position;
        pos = assigned === 'BENCH' || assigned === 'EH' ? 'BN' : (assigned || 'BN');
      } else {
        pos = player.primaryPosition || '';
      }
      return { slot: idx + 1, player, pos, isSP };
    });

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Lineup, ${game.isHome ? 'vs' : '@'} ${game.opponent}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif;
      background: #fff; color: #0D0D0D;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    @page { size: letter portrait; margin: 0.6in 0.5in; }
    .header { margin-bottom: 18px; }
    .team-name { font-size: 22pt; font-weight: 700; line-height: 1.1; }
    .game-meta { font-size: 10pt; color: #444; margin-top: 6px; display: flex; flex-direction: column; gap: 3px; }
    .meta-row { display: flex; align-items: center; gap: 6px; }
    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    thead tr { border-top: 1.5px solid #0D0D0D; border-bottom: 1.5px solid #0D0D0D; }
    th {
      font-size: 7pt; font-weight: 600; color: #888;
      text-transform: uppercase; letter-spacing: 0.08em;
      padding: 6px 8px; text-align: center;
    }
    th.col-player { text-align: left; }
    tbody tr { border-bottom: 0.5px solid #E0DED9; }
    tbody tr:last-child { border-bottom: 1.5px solid #0D0D0D; }
    td { padding: 10px 8px; vertical-align: middle; }
    td.col-slot  { font-size: 10pt; font-weight: 400; color: #888; text-align: center; width: 28pt; }
    td.col-name  { font-size: 12pt; font-weight: 600; }
    td.col-num   { font-size: 10pt; font-weight: 400; color: #888; text-align: center; width: 36pt; }
    td.col-pos   { font-size: 11pt; font-weight: 700; text-align: center; width: 40pt; }
    td.col-sub   { width: 120pt; border-left: 0.5px solid #E0DED9; }
    td.col-subnum { width: 36pt; border-left: 0.5px solid #E0DED9; }
    td.col-subpos { width: 40pt; border-left: 0.5px solid #E0DED9; }
    .sub-line { border-bottom: 0.5px solid #AAAAAA; display: block; width: 100%; margin-top: 16px; }
    .footer {
      margin-top: 14px; display: flex; justify-content: space-between;
      font-size: 7pt; color: #AAAAAA; border-top: 0.5px solid #E0DED9; padding-top: 8px;
    }
    .coaches { margin-top: 18px; display: flex; gap: 48pt; }
    .coach-line { border-bottom: 0.5px solid #AAAAAA; width: 180pt; margin-top: 20px; }
    .coach-label { font-size: 8pt; font-weight: 600; color: #0D0D0D; margin-top: 4px; }
  </style>
</head>
<body>
  <!-- Header -->
  <div class="header">
    <div class="team-name">${team.name}</div>
    <div class="game-meta">
      <div class="meta-row">vs. ${game.opponent}</div>
      ${game.location ? `<div class="meta-row">${game.location}</div>` : ''}
      <div class="meta-row">${dateStr}${game.time ? ` · ${game.time}` : ''}</div>
    </div>
  </div>

  <!-- Lineup table -->
  <table>
    <thead>
      <tr>
        <th style="width:28pt"></th>
        <th class="col-player">Player</th>
        <th>#</th>
        <th>Pos.</th>
        <th class="col-sub" style="text-align:left">Subs</th>
        <th class="col-subnum">#</th>
        <th class="col-subpos">Pos.</th>
      </tr>
    </thead>
    <tbody>
      ${rows.map(({ slot, player, pos, isSP }) => `
        <tr${isSP ? ' style="border-left:3px solid #C9A84C;"' : ''}>
          <td class="col-slot">${slot}</td>
          <td class="col-name">${player.name}${isSP ? ' <span style="font-size:7pt;font-weight:600;color:#C9A84C;background:#F5EDD6;padding:1px 4px;border-radius:3px;vertical-align:middle;">SP</span>' : ''}</td>
          <td class="col-num">${player.number}</td>
          <td class="col-pos">${pos || ''}</td>
          <td class="col-sub"><span class="sub-line"></span></td>
          <td class="col-subnum"><span class="sub-line"></span></td>
          <td class="col-subpos"><span class="sub-line"></span></td>
        </tr>
      `).join('')}
    </tbody>
  </table>

  <!-- Coach signature lines -->
  <div class="coaches">
    <div>
      <div class="coach-line"></div>
      <div class="coach-label">Head Coach</div>
    </div>
    <div>
      <div class="coach-line"></div>
      <div class="coach-label">Addtl. Coaches</div>
    </div>
  </div>

  <!-- Footer -->
  <div class="footer">
    <span>dugoutbaseball.app</span>
    <span>Dugout Baseball. Send chaos to the other dugout, not yours.</span>
  </div>
</body>
</html>`;

    try {
      setShareError(null);
      await printOrShareHtml(html, {
        fileName: `lineup-${game.isHome ? 'vs' : 'at'}-${game.opponent || 'opponent'}`,
        title: `Lineup ${game.isHome ? 'vs' : '@'} ${game.opponent || ''}`.trim(),
        orientation: 'portrait',
        webWindow: { width: 700, height: 900 },
      });
    } catch (e: any) {
      setShareError(e?.message || 'Could not prepare the lineup card.');
    }
  };

  // ── Lineup card import ─────────────────────────────────────────────────────
  const [isImportingLineup, setIsImportingLineup] = useState(false);
  const [importLineupError, setImportLineupError] = useState<string | null>(null);

  const handleLineupCardImport = async (file: File) => {
    setIsImportingLineup(true);
    setImportLineupError(null);
    try {
      const pages = await loadPdfPages(file);
      const result = parseLineupCard(pages, players);

      if (result.entries.length === 0) {
        setImportLineupError('No lineup entries found. Make sure this is a GameChanger lineup card PDF.');
        return;
      }

      // Build new player order: matched players in batting-order slot sequence,
      // then any roster players not in the card appended at the end.
      const matchedIds = result.entries.map(e => e.playerId!);
      const remaining = players.filter((p: any) => !matchedIds.includes(p.id));
      const newOrder = [...matchedIds, ...remaining.map((p: any) => p.id)];
      const newPlanPlayers = newOrder.map(id => players.find((p: any) => p.id === id)).filter(Boolean);
      setPlanPlayers(newPlanPlayers);

      // Set starting pitcher from the card (whoever has position 'P' in slot 1+)
      const pitcherEntry = result.entries.find(e => e.position === 'P');
      if (pitcherEntry?.playerId) setSelectedPitcherId(pitcherEntry.playerId);

      // Apply inning-1 defensive positions from the card
      const FIELD_POSITIONS = new Set(['P','C','1B','2B','3B','SS','LF','CF','RF','LCF','RCF']);
      const inning1 = result.entries
        .filter(e => e.playerId && FIELD_POSITIONS.has(e.position))
        .map(e => ({ id: e.playerId!, position: e.position }));
      if (inning1.length > 0) {
        setInnings(prev => {
          const next = [...prev];
          next[0] = inning1;
          return next;
        });
      }

      // Auto-save so the imported order persists
      const playerOrder = newPlanPlayers.map((p: any) => p.id);
      await setDoc(
        doc(db, `teams/${team.id}/games`, game.id),
        { plan: { playerOrder, startingPitcherId: pitcherEntry?.playerId || selectedPitcherId, savedAt: new Date().toISOString() } },
        { merge: true }
      );

      if (result.unmatched.length > 0) {
        setImportLineupError(`Imported ${result.entries.length} players. ${result.unmatched.length} name${result.unmatched.length !== 1 ? 's' : ''} on the card didn't match your roster: ${result.unmatched.map(e => e.name).join(', ')}`);
      }
    } catch (err) {
      setImportLineupError('Could not read the PDF. Make sure it\'s a GameChanger lineup card.');
      console.error('Lineup card import error:', err);
    } finally {
      setIsImportingLineup(false);
    }
  };

  const showPitcher = !team.settings?.noPitcher;
  const eligiblePitchers = planPlayers.filter((p: any) => (p.positions?.P || 0) > 0);
  const pitcherOptions = eligiblePitchers.length > 0 ? eligiblePitchers : planPlayers;
  const selectedPitcher = planPlayers.find((p: any) => p.id === selectedPitcherId);

  // "See More Players": roster players who aren't tagged pitchers. When some arms
  // ARE tagged, the dropdown only shows those — so an un-scouted kid can't be picked.
  // This is the escape hatch. Picking one tags them as a pitcher (positions.P = 1)
  // so they show in the bullpen next time. We never touch pitching attributes.
  const morePlanPitchers = planPlayers.filter(
    (p: any) => !(p.positions?.P > 0) && !pitcherOptions.some((o: any) => o.id === p.id)
  );

  const selectMorePitcher = async (p: any) => {
    tapLight(); // picking a different starting pitcher
    setSelectedPitcherId(p.id);
    setIsPitcherDropdownOpen(false);
    setShowMorePlanPitchers(false);
    if (!(p.positions?.P > 0)) {
      setPlanPlayers(prev => prev.map((pl: any) =>
        pl.id === p.id ? { ...pl, positions: { ...(pl.positions ?? {}), P: 1 } } : pl
      ));
      try {
        await setDoc(
          doc(db, `teams/${team.id}/players`, p.id),
          { positions: { ...(p.positions ?? {}), P: 1 }, updatedAt: new Date().toISOString() },
          { merge: true }
        );
      } catch (error) {
        handleFirestoreError(error, OperationType.UPDATE, `teams/${team.id}/players`);
      }
    }
    const rest = pitcherRest(p);
    if (rest) {
      setAlertModal({ title: 'Pitcher on Mandatory Rest', message: restAlertMessage(p.name, rest) });
    }
  };

  const gameDate = new Date(game.date);
  gameDate.setHours(0, 0, 0, 0);

  // Rest eligibility for the starter dropdown. Compute it the same way BullpenTab
  // does — call getPitcherEligibility() on the pitchingLog. (Previously this read a
  // `lastEntry.eligibleDate` field that the log never actually stores — it's a
  // computed output, not stored data — so the rest check silently never fired and
  // an over-rested pitcher could be set as starter with no alert.)
  const planRuleset = PITCH_COUNT_RULESETS[team.settings?.pitchCountRuleset || 'little_league'];
  const planAgeGroup = team.settings?.pitchCountAgeGroup || team.ageGroup || '12U';
  const planHasPitchCounts = team.styleOfPlay === 'playerspitch' && !!planRuleset;

  /** Rest status for the game date. null = eligible. Otherwise { until } — a
   *  formatted "available again" date, or '' when the org blocks the day without a
   *  concrete return date (e.g. USSSA innings-based next-day block). */
  const pitcherRest = (pitcher: any): { until: string } | null => {
    if (!planHasPitchCounts || !pitcher) return null;
    const result = getPitcherEligibility(
      pitcher.pitchingLog || [], planRuleset, planAgeGroup, gameDate, 0, game.tournamentId, true,
    );
    if (result.eligible) return null;
    const d = result.restStatus?.eligibleDate ? new Date(result.restStatus.eligibleDate) : null;
    return { until: d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '' };
  };

  /** Alert message for an on-rest pitcher — reads naturally whether or not we have
   *  a concrete return date. */
  const restAlertMessage = (name: string, rest: { until: string }): string =>
    rest.until
      ? `${name} is not available to pitch until ${rest.until}. You can still plan with them, but check your lineup before game time.`
      : `${name} is ineligible to pitch today. You can still plan with them, but check your lineup before game time.`;

  const absentPlayers = planPlayers.filter((p: any) => absentPlayerIds.includes(p.id));

  const isPlanReady = game.plan?.isReady;

  // ── Wizard derived state ──────────────────────────────────────────────────

  // The step sequence and its navigation live in ./plan/steps.ts so the
  // style-of-play gate can be tested without standing up the screen.
  // `planHasPitchCounts` is declared above and already means "players pitch".
  const stepSequence = stepSequenceFor(planHasPitchCounts);
  const stepLabel = (s: PlanScreen) => stepLabelFor(stepSequence, s);
  // Walking the steps always lands the defense step on inning 1; only a tap on
  // a specific inning in the review card opens it elsewhere.
  const goTo = (to: PlanScreen) => { if (to === 'defense') setDefenseInning(0); setScreen(to); };
  const goNext = (from: PlanScreen) => goTo(nextScreen(stepSequence, from));
  const goPrev = (from: PlanScreen) => goTo(prevScreen(stepSequence, from));

  /** Advance and persist. Progressive save is what makes "Save and Exit" honest. */
  const advance = (from: PlanScreen) => { void handleSave(false); goNext(from); };

  const activePlayers = useMemo(
    () => planPlayers
      .filter((p: any) => !absentPlayerIds.includes(p.id))
      .map((p: any) => ({ ...p, stats: playerSeasonStats?.get(p.id) ?? p.stats })),
    [planPlayers, absentPlayerIds, playerSeasonStats],
  );

  const gamesById = useMemo(() => new Map(allGames.map(g => [g.id, g])), [allGames]);

  // The rich pitching rollup, NOT the flat playerSeasonStats map from App.tsx.
  // Only this one carries pitches-per-inning, S%, FPS% and runs allowed, and it
  // is the same source PitchingCommandCenter reads, so the Game Planner and the
  // Stats tab can never disagree about a pitcher.
  const seasonRollup = useMemo(
    () => (allGames.length ? buildSeasonStats(planPlayers, allGames, { fieldTimeSource: fieldTimeSourceOf(team), defaultInnings: team?.defaultInnings }).stats : {}),
    [planPlayers, allGames, team?.settings?.fieldTimeSource, team?.defaultInnings],
  );
  const seasonRollupMap = useMemo(
    () => new Map(Object.entries(seasonRollup)),
    [seasonRollup],
  );

  const pitcherRows = useMemo(
    () => buildPitcherReadiness(
      activePlayers, planRuleset, planAgeGroup, new Date(game.date), game.tournamentId,
      seasonRollupMap, gamesById, planHasPitchCounts,
    ),
    [activePlayers, planRuleset, planAgeGroup, game.date, game.tournamentId, seasonRollupMap, gamesById, planHasPitchCounts],
  );

  // Arriving on the defense step with nothing built reads as broken: the mode
  // dropdown already says "Smart", so a coach reasonably expects to see a
  // defense. Build one on arrival. Re-entering a step that already has innings
  // leaves the coach's own arrangement alone.
  //
  // The manual planner can be the FIRST screen (a manual plan reopens there),
  // so this can run before the saved plan has loaded. Wait for the roster, and
  // never build over a saved plan: building first would replace it with an
  // empty one once the timer fired.
  useEffect(() => {
    if (screen !== 'defense' && screen !== 'manual') return;
    if (planPlayers.length === 0) return;
    if (innings[0] && innings[0].length > 0) return;
    if (game.plan?.innings?.['0']?.length) return;
    if (isGenerating) return;
    handleFillDefense(defenseMode);
    // Intentionally keyed on arrival (and the roster arriving), not on the
    // lineup changing underneath it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, planPlayers.length > 0]);

  /** Most recent OTHER game that already carries a saved plan. */
  const lastPlannedGame = useMemo(() => {
    const target = new Date(game.date).getTime();
    return allGames
      .filter(g => g.id !== game.id && g.plan?.savedAt && new Date(g.date).getTime() <= target)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0] ?? null;
  }, [allGames, game.id, game.date]);

  const fieldTimeRows = useMemo(
    () => buildFieldTimeRows(planPlayers, playerFieldTimeStats),
    [planPlayers, playerFieldTimeStats],
  );
  const lightCount = fieldTimeRows.filter(r => r.due).length;
  // Unfolded iPhone Duo, iPad landscape, laptop: the defense grid becomes the
  // Lineup Wall with the plan's consequences beside it. Phones are unchanged.
  const isWide = useIsWide();
  const gamesCounted = Math.max(0, ...fieldTimeRows.map(r => r.gp));

  /** Advisory line naming who is owed innings. Never overrides the lineup. */
  const fairnessNote = useMemo(() => {
    const owed = fieldTimeRows.filter(r => r.due).slice(0, 2).map(r => r.player.name.split(' ')[0]);
    if (owed.length === 0) return null;
    const who = owed.length === 1 ? owed[0] : `${owed[0]} and ${owed[1]}`;
    return `${who} ${owed.length === 1 ? 'is' : 'are'} owed innings, so starting ${owed.length === 1 ? 'him' : 'them'} keeps the bench even.`;
  }, [fieldTimeRows]);

  const pitcherPlanIds = [selectedPitcherId, ...relievers];

  /**
   * Who the bullpen plan puts on the mound each inning. A planned arm the
   * readiness rows do not list (no pitching history yet) still gets the
   * step's default two-inning span rather than being dropped.
   */
  const plannedArmsByInning = (starterId: string | null, relieverIds: (string | null)[], count = numInnings) => {
    const byId = new Map(pitcherRows.map(r => [r.player.id, r]));
    const arms = [starterId, ...relieverIds].map(id => {
      if (!id) return null;
      return byId.get(id) ?? ({ player: { id }, forecastIP: null } as any);
    });
    return plannedPitcherByInning(arms, count);
  };

  /**
   * Put each inning's planned arm on the mound. A swap, so the pitcher he
   * replaces takes his spot in the field and no position is left empty. Only
   * players-pitch teams have a bullpen plan; everyone else is untouched.
   */
  const applyPitcherPlan = (starterId: string | null, relieverIds: (string | null)[]) => {
    if (!planHasPitchCounts || team.settings?.noPitcher) return;
    const arms = plannedArmsByInning(starterId, relieverIds);
    setInnings(prev => {
      // Bench innings so far in this plan, so a hole is filled by whoever has sat most.
      const benchCount = new Map<string, number>();
      prev.forEach(inn => (inn || []).forEach((p: any) => {
        if (p.position === 'BENCH') benchCount.set(p.id, (benchCount.get(p.id) || 0) + 1);
      }));
      return prev.map((inning, idx) => {
        const armId = arms[idx];
        const arm = inning?.find((p: any) => p.id === armId);
        if (!inning?.length || !armId || !arm) return inning;
        const moundWasEmpty = !inning.some((p: any) => p.position === 'P');
        const vacated = arm.position;
        let next = swapIntoPosition(inning, 'P', armId);
        // With nobody on the mound there is no pitcher to hand the arm's spot
        // to, so the swap leaves it empty. Fill it from the bench instead.
        if (moundWasEmpty && vacated !== 'P' && vacated !== 'BENCH' && vacated !== 'EH') {
          const sub = next
            .filter((p: any) => p.position === 'BENCH')
            .sort((a: any, b: any) => (benchCount.get(b.id) || 0) - (benchCount.get(a.id) || 0))[0];
          if (sub) next = swapIntoPosition(next, vacated, sub.id);
        }
        return next;
      });
    });
  };

  const setPitcherSlot = (index: number, playerId: string | null) => {
    if (index === 0) {
      setSelectedPitcherId(playerId);
      applyPitcherPlan(playerId, relievers);
      return;
    }
    const next = [...relievers];
    next[index - 1] = playerId;
    setRelievers(next);
    applyPitcherPlan(selectedPitcherId, next);
  };

  /**
   * Review-card bullpen summary, read from who is actually at P each inning,
   * so it always agrees with the defense printed above it, including any
   * inning the coach set by hand. Before a defense exists it falls back to the
   * bullpen step's forecast.
   */
  const reviewPitcherPlan = useMemo(() => {
    const nameOf = (id: string) =>
      (planPlayers.find((p: any) => p.id === id)?.name || '').split(' ')[0] || '?';
    const atP = Array.from({ length: numInnings }, (_, i) =>
      (innings[i] || []).find((p: any) => p.position === 'P')?.id ?? null);

    if (atP.some(Boolean)) {
      const runs: { name: string; from: number; through: number }[] = [];
      atP.forEach((id, i) => {
        if (!id) return;
        const last = runs[runs.length - 1];
        if (last && last.through === i && atP[i - 1] === id) last.through = i + 1;
        else runs.push({ name: nameOf(id), from: i + 1, through: i + 1 });
      });
      return { runs, open: atP.filter(id => !id).length };
    }

    const arms = plannedArmsByInning(selectedPitcherId, relievers);
    const runs: { name: string; from: number; through: number }[] = [];
    let inning = 0;
    pitcherPlanIds.forEach(id => {
      if (!id) return;
      const span = arms.filter(a => a === id).length;
      if (!span) return;
      runs.push({ name: nameOf(id), from: inning + 1, through: inning + span });
      inning += span;
    });
    return { runs, open: runs.length ? 0 : numInnings };
  }, [innings, numInnings, planPlayers, pitcherPlanIds.join(','), pitcherRows]);

  /** Review-card fairness summary for THIS game's plan. */
  const benchSummary = useMemo(() => {
    if (!innings.length) return null;
    const counts = new Map<string, number>();
    innings.slice(0, numInnings).forEach((inn: any[]) => {
      (inn || []).forEach((p: any) => {
        if (p.position === 'BENCH') counts.set(p.id, (counts.get(p.id) || 0) + 1);
      });
    });
    if (counts.size === 0) return 'Nobody sits';
    const values = [...counts.values()];
    const max = Math.max(...values);
    const min = Math.min(...values);
    return max === min ? `${max} each, nobody sits twice` : `${min} to ${max} innings`;
  }, [innings, numInnings]);

  // ── Entry-path handlers ───────────────────────────────────────────────────

  /** Run every engine end to end, then land on the review card. */
  const handleAutoGenerate = () => {
    tapMedium();
    let starterId = selectedPitcherId;
    if (planHasPitchCounts && !starterId) {
      const rec = pitcherRows.find(r => r.recommended);
      if (rec) { starterId = rec.player.id; setSelectedPitcherId(starterId); }
    }
    handleFillDefense(defenseMode, starterId);
    handleApplyOrder(orderMode === 'None' ? 'OPS First' : orderMode);
    if (orderMode === 'None') setOrderMode('OPS First');
    setScreen('review');
  };

  /**
   * Copy the previous game's plan forward. Availability and the bullpen are NOT
   * copied: who turned up is specific to today, and last game's relievers may be
   * on mandated rest now. The starter is only carried over if he is eligible
   * today, which getPitcherEligibility already knows.
   */
  const handleUseLastGame = () => {
    const prev = lastPlannedGame?.plan;
    if (!prev) return;
    tapMedium();

    const loaded: any[] = [];
    Object.entries(prev.innings || {}).forEach(([k, v]) => {
      const idx = parseInt(k, 10);
      if (!Number.isNaN(idx)) loaded[idx] = v as any;
    });
    setInnings(loaded);
    setNumInnings(prev.numInnings || numInnings);

    if (prev.playerOrder) {
      const ordered = prev.playerOrder.map((id: string) => players.find((p: any) => p.id === id)).filter(Boolean);
      const remaining = players.filter((p: any) => !prev.playerOrder.includes(p.id));
      setPlanPlayers([...ordered, ...remaining]);
    }

    const carried = prev.startingPitcherId
      ? pitcherRows.find(r => r.player.id === prev.startingPitcherId && r.status !== 'rest')
      : null;
    setSelectedPitcherId(carried ? carried.player.id : null);
    setRelievers([null, null]);

    setScreen('review');
  };

  const closeWizard = () => { void handleCloseWithAutosave(); };

  // ── Manual planner ────────────────────────────────────────────────────────

  /** Open a side screen from the manual planner, remembering to come back. */
  const openFromManual = (to: PlanScreen) => { setBackTo('manual'); setScreen(to); };
  const returnFromSide = () => {
    const to = backTo ?? 'entry';
    setBackTo(null);
    void handleSave(false);
    setScreen(to);
  };

  /**
   * Starter picked from the manual planner's sheet. Same effect as the
   * wizard's pitcher step: he goes on the mound for his innings across the
   * grid. An on-rest pick is allowed with a warning, as the old planner did.
   */
  const handleManualPitcher = (playerId: string) => {
    tapLight();
    setSelectedPitcherId(playerId);
    applyPitcherPlan(playerId, relievers);
    const player = planPlayers.find((p: any) => p.id === playerId);
    const rest = pitcherRest(player);
    if (rest) setAlertModal({ title: 'Pitcher on Mandatory Rest', message: restAlertMessage(player.name, rest) });
  };

  /**
   * Naming a starter from the detail sheet both sets him and moves on, because
   * "Start Marcus" reads as a decision, not a selection. The explicit override
   * on the save is required: setSelectedPitcherId has not landed yet in this tick.
   */
  const chooseStarterAndContinue = (playerId: string) => {
    tapMedium();
    setSelectedPitcherId(playerId);
    // Put him on the mound now rather than waiting for the defense to rebuild,
    // so step 2 opens with him already at P.
    applyPitcherPlan(playerId, relievers);
    void handleSave(false, { startingPitcherId: playerId });
    goNext('pitcher');
  };

  /**
   * Assign one player to a position in one inning from the defense step's
   * per-player selector. Reuses swapIntoPosition, so the position a player
   * leaves is handed to whoever held the one he is taking. Sending a player to
   * BENCH or EH simply vacates his old spot: those are shared and hold any
   * number, so nobody is swapped back.
   */
  const handleSetPosition = (inningIdx: number, playerId: string, position: string) => {
    tapLight();
    setDefenseEdited(true);
    setInnings(prev => {
      const next = [...prev];
      const base = next[inningIdx]?.length
        ? next[inningIdx]
        : activePlayers.map((p: any) => ({ id: p.id, position: 'BENCH' }));
      next[inningIdx] = swapIntoPosition(base, position, playerId);
      return next;
    });
    // Keep the starter in sync with inning 1: otherwise the engine's pitcher
    // reconciliation would put the old starter straight back at P on the next
    // regenerate. A change at P in a later inning is a relief decision and
    // shows up on the review card's bullpen line, read from the grid.
    if (inningIdx !== 0) return;
    if (position === 'P') setSelectedPitcherId(playerId);
    else if (playerId === selectedPitcherId) setSelectedPitcherId(null);
  };

  /**
   * Add an inning to the end of the plan (a 7-inning high school game, extra
   * innings). The engine builds it from the innings before it, so bench time
   * keeps rotating fairly, and the bullpen plan decides who pitches it.
   */
  const handleAddInning = () => {
    if (numInnings >= MAX_PLAN_INNINGS) return;
    tapLight();
    const nextCount = numInnings + 1;
    const settings = team.settings || {};
    const pitcherId = planHasPitchCounts
      ? plannedArmsByInning(selectedPitcherId, relievers, nextCount)[numInnings] ?? null
      : selectedPitcherId;
    const previous = innings.slice(0, numInnings);
    const added = previous.length === numInnings && previous.every(inn => inn?.length)
      ? buildInning(numInnings, previous, pitcherId, defenseSettingsFor(defenseMode, settings))
      : null;
    setInnings(prev => {
      const next = prev.slice(0, numInnings);
      if (added) next[numInnings] = added;
      return next;
    });
    setNumInnings(nextCount);
  };

  /**
   * Drop the last inning, for coaches who only plan the first few. The inning
   * is removed from the saved plan too, because the live game counts the
   * plan's innings to know how long the game is.
   */
  const handleRemoveInning = () => {
    if (numInnings <= MIN_PLAN_INNINGS) return;
    tapLight();
    const nextCount = numInnings - 1;
    setInnings(prev => prev.slice(0, nextCount));
    setNumInnings(nextCount);
    inningsTrimmed.current = true;
  };

  /** Copy one inning's defense onto every inning after it. */
  const handleCopyToLaterInnings = (inningIdx: number) => {
    const source = innings[inningIdx];
    if (!source?.length) return;
    tapMedium();
    setDefenseEdited(true);
    setInnings(prev => {
      const next = [...prev];
      for (let i = inningIdx + 1; i < numInnings; i++) next[i] = source.map((p: any) => ({ ...p }));
      return next;
    });
  };

  /** The Lineup Wall's right pane, for the defense step and the manual planner. */
  const wallSide = (onFieldTime: () => void) => (
    <PlanConsequences
      players={activePlayers}
      innings={innings}
      numInnings={numInnings}
      settings={team.settings}
      seasonRows={fieldTimeRows}
      showPitching={planHasPitchCounts && !team.settings?.noPitcher}
      pitcherRows={pitcherRows}
      onFieldTime={onFieldTime}
    />
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-[100] flex flex-col safe-top safe-x bg-canvas"
    >
      {screen === 'entry' && (
        <PlanEntry
          game={game}
          team={team}
          presentCount={planPlayers.length - absentPlayerIds.length}
          absentCount={absentPlayerIds.length}
          lastPlannedGame={lastPlannedGame}
          onAvailability={() => setScreen('availability')}
          onAutoGenerate={() => { setPlannerMode('wizard'); handleAutoGenerate(); }}
          onStepByStep={() => { setPlannerMode('wizard'); goTo(stepSequence[0]); }}
          onUseLast={() => { setPlannerMode('wizard'); handleUseLastGame(); }}
          onManual={() => { tapLight(); setPlannerMode('manual'); setScreen('manual'); }}
          onImport={handleLineupCardImport}
          isImporting={isImportingLineup}
          onClose={closeWizard}
        />
      )}

      {screen === 'availability' && (
        <AvailabilityScreen
          players={planPlayers}
          absentPlayerIds={absentPlayerIds}
          onToggleAbsent={handleToggleAbsent}
          onDone={() => { setScreen(backTo ?? 'entry'); setBackTo(null); }}
        />
      )}

      {screen === 'pitcher' && (
        <StepPitcher
          rows={pitcherRows}
          ageGroup={planAgeGroup}
          stepLabel={stepLabel('pitcher')}
          selectedPitcherId={selectedPitcherId}
          onSelect={(id) => { tapLight(); setSelectedPitcherId(id); applyPitcherPlan(id, relievers); }}
          onStartAndContinue={chooseStarterAndContinue}
          onBack={() => goPrev('pitcher')}
          onContinue={() => advance('pitcher')}
          onSkip={() => goNext('pitcher')}
        />
      )}

      {screen === 'defense' && (
        <StepDefense
          innings={innings}
          numInnings={numInnings}
          focusInning={defenseInning}
          onCopyToLaterInnings={handleCopyToLaterInnings}
          onAddInning={numInnings < MAX_PLAN_INNINGS ? handleAddInning : undefined}
          onRemoveInning={numInnings > MIN_PLAN_INNINGS ? handleRemoveInning : undefined}
          defenseEdited={defenseEdited}
          players={planPlayers.filter((p: any) => !absentPlayerIds.includes(p.id))}
          stepLabel={stepLabel('defense')}
          defenseMode={defenseMode}
          onDefenseMode={(m) => { setDefenseMode(m); handleFillDefense(m); }}
          isGenerating={isGenerating}
          fairnessNote={fairnessNote}
          lightCount={lightCount}
          selectedPitcherId={selectedPitcherId}
          settings={team.settings}
          onReplacePitcher={() => setScreen('pitcher')}
          onFieldTime={() => setScreen('fieldtime')}
          onSetPosition={handleSetPosition}
          wide={isWide}
          side={wallSide(() => setScreen('fieldtime'))}
          onBack={() => goPrev('defense')}
          onContinue={() => advance('defense')}
          onSkip={() => goNext('defense')}
        />
      )}

      {screen === 'fieldtime' && (
        <SeasonFieldTime
          players={planPlayers}
          playerFieldTimeStats={playerFieldTimeStats}
          team={team}
          gamesCounted={gamesCounted}
          onBack={() => (backTo ? returnFromSide() : setScreen('defense'))}
        />
      )}

      {screen === 'order' && (
        <StepOrder
          activePlayers={activePlayers}
          batterSignals={batterSignals}
          orderMode={orderMode}
          orderNote={orderNote}
          stepLabel={stepLabel('order')}
          onApplyOrder={(m) => { setOrderMode(m); handleApplyOrder(m); }}
          onReorder={handleReorderActive}
          onBack={() => goPrev('order')}
          onContinue={() => advance('order')}
          onSkip={() => goNext('order')}
        />
      )}

      {screen === 'bullpen' && (
        <StepBullpen
          rows={pitcherRows}
          pitcherPlan={pitcherPlanIds}
          numInnings={numInnings}
          stepLabel={stepLabel('bullpen')}
          onSetSlot={setPitcherSlot}
          onBack={() => (backTo ? returnFromSide() : goPrev('bullpen'))}
          onContinue={() => (backTo ? returnFromSide() : advance('bullpen'))}
          onSkip={() => (backTo ? returnFromSide() : goNext('bullpen'))}
        />
      )}

      {screen === 'review' && (
        <StepReview
          game={game}
          players={planPlayers}
          innings={innings}
          numInnings={numInnings}
          orderedPlayers={activePlayers}
          pitcherPlan={reviewPitcherPlan}
          benchSummary={benchSummary}
          stepLabel={stepLabel('review')}
          canBeginGame={!!onBeginGame && game.status !== 'completed'}
          hasScouting={!!opponentScout}
          onPrintPlan={handlePrint}
          onPrintCard={handleLineupCardExport}
          onScouting={() => setScreen('scouting')}
          onJump={(s, inning) => {
            if (s === 'defense') setDefenseInning(inning ?? 0);
            setScreen(s);
          }}
          onBack={() => goPrev('review')}
          onBeginGame={async () => {
            tapMedium();
            await handleBeginGame();
          }}
          onSaveAndExit={() => { void handleSave(false); onClose(); }}
        />
      )}

      {screen === 'manual' && (
        <ManualPlan
          game={game}
          team={team}
          players={activePlayers}
          innings={innings}
          numInnings={numInnings}
          settings={team.settings}
          presentCount={planPlayers.length - absentPlayerIds.length}
          absentCount={absentPlayerIds.length}
          showPitching={planHasPitchCounts}
          pitcherRows={pitcherRows}
          selectedPitcherId={selectedPitcherId}
          bullpenSummary={reviewPitcherPlan.runs.length
            ? reviewPitcherPlan.runs.map(a => a.from === a.through ? `${a.name} ${a.from}` : `${a.name} ${a.from} to ${a.through}`).join(', ')
            : null}
          orderMode={orderMode}
          batterSignals={batterSignals}
          defenseMode={defenseMode}
          defenseEdited={defenseEdited}
          isGenerating={isGenerating}
          canBeginGame={!!onBeginGame && game.status !== 'completed'}
          hasScouting={!!opponentScout}
          onAvailability={() => openFromManual('availability')}
          onSelectPitcher={handleManualPitcher}
          onBullpen={() => openFromManual('bullpen')}
          onApplyOrder={(m) => { setOrderMode(m); handleApplyOrder(m); }}
          onDefenseMode={(m) => { setDefenseMode(m); handleFillDefense(m); }}
          onReorder={handleReorderActive}
          onSetPosition={handleSetPosition}
          onCopyToLaterInnings={handleCopyToLaterInnings}
          onAddInning={numInnings < MAX_PLAN_INNINGS ? handleAddInning : undefined}
          onRemoveInning={numInnings > MIN_PLAN_INNINGS ? handleRemoveInning : undefined}
          onPrintPlan={handlePrint}
          onPrintCard={handleLineupCardExport}
          onScouting={() => openFromManual('scouting')}
          onBack={() => { void handleSave(false); setScreen('entry'); }}
          onBeginGame={async () => {
            tapMedium();
            await handleBeginGame();
          }}
          onSaveAndExit={() => { void handleSave(false); onClose(); }}
          wide={isWide}
          side={wallSide(() => openFromManual('fieldtime'))}
        />
      )}

      {screen === 'scouting' && (
        <>
          <WizardBar label="Scouting" onBack={() => (backTo ? returnFromSide() : setScreen(game.plan?.savedAt ? 'review' : 'entry'))} />
          <div className="flex-1 overflow-y-auto bg-canvas">
            <ScoutingTab opponentScout={opponentScout} game={game} />
          </div>
        </>
      )}

      {shareError && (
        <div role="status" className="absolute left-4 right-4 bottom-28 bg-surface border border-hairline rounded-card px-4 py-3 text-[13px] font-semibold text-danger shadow-lg">
          {shareError}
        </div>
      )}

      {importLineupError && (
        <div className="absolute left-4 right-4 bottom-28 bg-gold-soft border border-gold rounded-card px-4 py-3 flex items-start gap-3">
          <span className="flex-1 text-[13px] text-gold-ink">{importLineupError}</span>
          <button onClick={() => setImportLineupError(null)} className="text-gold-ink text-base leading-none">×</button>
        </div>
      )}

      {/* Pitch Rest Alert Modal */}
      <AnimatePresence>
        {alertModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-end justify-center pb-8 px-4"
            onClick={() => setAlertModal(null)}
          >
            <motion.div
              initial={{ y: 40, scale: 0.96 }}
              animate={{ y: 0, scale: 1 }}
              exit={{ y: 40, scale: 0.96 }}
              className="w-full max-w-sm bg-surface border border-hairline rounded-panel p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Converted off the inverted zinc idiom to semantic tokens, which
                  is what takes this screen out of the legacy-invert quarantine.
                  Each mapping keeps the colour it rendered as under the
                  inversion: zinc-900 was white, zinc-300 was dark grey, and so
                  on. amber-400 becomes gold-ink because amber was never part of
                  the inversion and fails AA as text on a light surface. */}
              <p className="text-xs font-bold uppercase tracking-widest text-gold-ink mb-1">{alertModal.title}</p>
              <p className="text-sm text-ink-2 leading-relaxed mb-4">{alertModal.message}</p>
              <button
                onClick={() => setAlertModal(null)}
                className="w-full py-2.5 rounded-control bg-raised text-xs font-bold uppercase tracking-widest text-ink hover:bg-track transition-colors"
              >
                Got It
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </motion.div>
  );
};
